import * as React from "react"
import { Link, router, usePage } from "@inertiajs/react"
import { Check, ChevronUp, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { QuantityStepper } from "@/components/ui/quantity-stepper"
import { formatPence } from "@/lib/money"
import { cn } from "@/lib/utils"
import type { PageProps, Selection } from "@/types/inertia"
import "@/styles/public-order.css"

// The floating selection tray (M3): a fixed bar on mobile, a bottom-right card
// on desktop that expands to list the lines with steppers. Rendered by
// PublicShell on every public page once the selection holds a bottle; hidden
// on the order pages themselves, which show the same information in full.

export function bottles(count: number) {
  return `${count} ${count === 1 ? "bottle" : "bottles"}`
}

/** "62 of 100 bottles — add at least 38 more", or null once the minimum is met. */
export function shortfallCopy(selection: Selection) {
  if (selection.minimum_met) return null
  return `${selection.bottle_count} of ${selection.minimum} bottles — add at least ${selection.shortfall} more`
}

/** The estimate line: a full sum, or the honest nil-price copy. Never partial. */
export function EstimateLine({ pence, className }: { pence: number | null; className?: string }) {
  return (
    <div className={cn("sel-estimate", className)}>
      {pence === null ? (
        "Estimate available once pricing is confirmed"
      ) : (
        <>
          Estimate <span className="sel-estimate-figure">{formatPence(pence)}</span>
          <span className="sel-estimate-note"> — final quote at consultation</span>
        </>
      )}
    </div>
  )
}

/** Progress towards the 100-bottle minimum, with the locked copy under it. */
export function MinimumProgress({ selection }: { selection: Selection }) {
  const copy = shortfallCopy(selection)
  return (
    <div className="sel-progress">
      <Progress
        value={selection.bottle_count}
        max={selection.minimum}
        label={`Bottles towards the ${selection.minimum}-bottle minimum`}
        valueText={`${selection.bottle_count} of ${selection.minimum} bottles`}
      />
      <div className="sel-progress-copy" aria-live="polite">
        {copy ?? (
          <span className="sel-minimum-met">
            <Check className="h-3.5 w-3.5" aria-hidden="true" /> Minimum reached
          </span>
        )}
      </div>
    </div>
  )
}

export function setLineCount(wineId: number, count: number) {
  router.patch(
    `/selection/lines/${wineId}`,
    { bottle_count: String(count) },
    { preserveScroll: true, preserveState: true },
  )
}

export function removeLine(wineId: number) {
  router.delete(`/selection/lines/${wineId}`, { preserveScroll: true, preserveState: true })
}

function isOrderPage(url: string) {
  const path = url.split(/[?#]/)[0]
  return path === "/order" || path.startsWith("/order/")
}

export function SelectionTray() {
  const { props, url } = usePage<PageProps>()
  const selection = props.selection
  const [expanded, setExpanded] = React.useState(false)
  const panelId = React.useId()

  if (!selection || selection.bottle_count <= 0 || isOrderPage(url)) return null

  return (
    <>
      {/* Keeps the fixed mobile bar from covering the end of the page. */}
      <div className="sel-tray-spacer" aria-hidden="true" />

      <aside className={cn("sel-tray", expanded && "is-expanded")} aria-label="Your selection">
        <div className="sel-tray-head">
          <div className="sel-tray-title">
            Your selection <span aria-hidden="true">·</span> {bottles(selection.bottle_count)}
          </div>
          <button
            type="button"
            className="sel-tray-toggle"
            aria-expanded={expanded}
            aria-controls={panelId}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? "Hide" : "View"}
            <span className="sr-only"> the wines in your selection</span>
            <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>

        {expanded && (
          <ul id={panelId} className="sel-tray-lines">
            {selection.lines.map((line) => (
              <li key={line.wine_id} className="sel-tray-line">
                <div className="min-w-0">
                  <div className="sel-tray-line-name">{line.name}</div>
                  <div className="sel-tray-line-meta">
                    {line.producer} · {line.wine_type_label}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <QuantityStepper
                    size="sm"
                    value={line.bottle_count}
                    label={`Bottles of ${line.name}`}
                    onChange={(next) => setLineCount(line.wine_id, next)}
                  />
                  <button
                    type="button"
                    className="sel-remove"
                    aria-label={`Remove ${line.name}`}
                    onClick={() => removeLine(line.wine_id)}
                  >
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <MinimumProgress selection={selection} />
        <EstimateLine pence={selection.estimated_total_pence} className="sel-tray-estimate" />

        <Button asChild variant="gold" size="sm" className="sel-tray-cta">
          <Link href="/order">Review selection</Link>
        </Button>
      </aside>
    </>
  )
}
